import * as React from 'react';

import { faCopy } from '@fortawesome/pro-regular-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { VersionForm } from 'src/features/reservations/components/modals/VersionForm.tsx';
import {
    createReservationTypeFormSchema,
    type CreateReservationTypeFormSchemaValues,
    useCreateReservationType,
} from 'src/features/reservations/hooks/useCreateReservationType.ts';
import { reservationMessages } from 'src/features/reservations/utils/messages.ts';
import SquareIcon from 'src/registry/components/blocks/SquareIcon.tsx';
import { toast } from 'src/registry/hooks/UseToast.ts';
import { Button } from 'src/registry/ui/Button.tsx';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog.tsx';
import { generalErrorMessages, generalMessages } from 'src/utils/messages.ts';

import type { ReservationType } from 'src/features/reservations/types/reservationType.ts';

type CopyVersionModalProps = {
    isCopyVersionModalOpen: boolean;
    closeCopyVersionModal: (isOpen: boolean) => void;
    selectedVersion: ReservationType;
    refetch: () => void;
};

const CopyVersionModal = ({
    isCopyVersionModalOpen,
    closeCopyVersionModal,
    selectedVersion,
    refetch,
}: CopyVersionModalProps) => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParams();

    const { mutate: createNewCopyVersion } = useCreateReservationType({
        onSuccess: () => {
            void refetch();
            toast({
                title: formatMessage(reservationMessages.versionCreateSuccess),
                description: formatMessage(reservationMessages.versionCreateSuccessDetails),
            });
        },
        onError: (error) => {
            const errorData = error.response?.data;
            const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const newestValidFromDate = new Date(selectedVersion.valid_from);
    const nextDate = new Date(newestValidFromDate);
    nextDate.setDate(nextDate.getDate() + 1); // pre-selects a valid from date: 1 day after most recent valid_from

    const form = useForm<CreateReservationTypeFormSchemaValues>({
        resolver: zodResolver(createReservationTypeFormSchema),
        mode: 'onChange',
        defaultValues: {},
    });

    React.useEffect(() => {
        if (selectedVersion) {
            form.reset({
                reservation_type: selectedVersion.reservation_type,
                valid_from: nextDate,
                price: selectedVersion.price,
                tax_amount: selectedVersion.tax_amount,
                tax_percent: selectedVersion.tax_percent,
                price_with_tax: selectedVersion.price_with_tax,
                currency: selectedVersion.currency,
                allowed_spaces: selectedVersion.allowed_spaces,
                entry_tolerance_minutes: selectedVersion.entry_tolerance_minutes,
                exit_tolerance_minutes: selectedVersion.exit_tolerance_minutes,
                reserve_space: selectedVersion.reserve_space,
                name: selectedVersion.name,
                description: selectedVersion.description,
            });
        }
    }, [selectedVersion]);

    const onSubmit = (values: CreateReservationTypeFormSchemaValues) => {
        const submitData = {
            parkingId: parkingId || '',
            ...values,
        };
        createNewCopyVersion(submitData);
        closeCopyVersionModal(!isCopyVersionModalOpen);
        form.reset();
    };

    return (
        <Dialog open={isCopyVersionModalOpen} onOpenChange={closeCopyVersionModal}>
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={faCopy} />
                        <div>
                            <DialogTitle>{`${formatMessage(reservationMessages.copy)} ${formatMessage(reservationMessages.version).toLocaleLowerCase()}`}</DialogTitle>
                            <DialogDescription>
                                {formatMessage(reservationMessages.copyVersionDetails)}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <VersionForm form={form} onSubmit={form.handleSubmit(onSubmit)} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            {formatMessage(generalMessages.cancel)}
                        </Button>
                    </DialogClose>
                    <Button
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                        disabled={!form.formState.isDirty}
                    >
                        {formatMessage(reservationMessages.addNewVersion)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default CopyVersionModal;
